import { currentLocale } from '@/lib/locale';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, RefreshCw, Trash2, FolderSync, AlertTriangle, LogIn } from 'lucide-react';
import {
  useKnowledgeBaseList,
  useKnowledgeDelete,
  useDriveSyncConfig,
  useDriveOAuthUrl,
  useConnectDriveAuth,
  useTriggerDriveSync,
} from '@/hooks/usePerformance';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function PerformanceAdminKnowledge() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: docs, isLoading } = useKnowledgeBaseList(null);
  const deleteDoc = useKnowledgeDelete();
  const { data: driveConfig, isLoading: configLoading } = useDriveSyncConfig();
  const { data: oauthUrlData } = useDriveOAuthUrl();
  const triggerSync = useTriggerDriveSync();
  const connectDrive = useConnectDriveAuth();

  const isConnected = driveConfig?.isConnected;
  const lastSync = driveConfig?.lastSyncAt
    ? new Date(driveConfig?.lastSyncAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
    : null;

  // Auto-connect when Google redirects back with ?code=
  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('code');
      next.delete('scope');
      next.delete('authuser');
      next.delete('prompt');
      return next;
    }, { replace: true });
    connectDrive.mutate({ code });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectGoogle = () => {
    if (oauthUrlData?.url) window.location.href = oauthUrlData.url;
  };

  const showReconnect = !isConnected || triggerSync.isError || connectDrive.isError;

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">{t('performance.admin.layout.knowledge_base')}</h1>
          {lastSync && (
            <p className="text-xs text-muted-foreground mt-0.5">{t('performance.adminknowledge.ultima_sincronizacion')} {lastSync}</p>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => triggerSync.mutate()}
          disabled={triggerSync.isPending || !isConnected || showReconnect}
        >
          {triggerSync.isPending
            ? <WinclapLoader size={14} className="mr-1.5" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          }
          {t('performance.adminknowledge.sincronizar_ahora')}
        </Button>
      </div>

      {/* Drive status */}
      {configLoading ? null : connectDrive.isPending ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <WinclapLoader size={14} />
          <span>{t('performance.adminknowledge.conectando_con_google_drive')}</span>
        </div>
      ) : showReconnect ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {triggerSync.isError
              ? t('performance.adminknowledge.token_expirado')
              : t('performance.adminknowledge.drive_no_conectado')}
          </div>
          <Button
            size="sm"
            disabled={!oauthUrlData?.url || connectDrive.isPending}
            onClick={handleConnectGoogle}
          >
            <LogIn className="h-3.5 w-3.5 mr-1.5" />
            {t('performance.adminknowledge.conectar_con_google')}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FolderSync className="h-3.5 w-3.5 text-emerald-500" />
          <span>{t('performance.adminknowledge.conectado_a_google_drive')}</span>
          {driveConfig?.folderName && (
            <span className="font-medium text-foreground">· {driveConfig?.folderName}</span>
          )}
        </div>
      )}

      {/* Document list */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <WinclapLoader size={32} className="text-foreground/25" />
        </div>
      ) : (docs ?? []).length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            {t('performance.adminknowledge.no_hay_documentos_sincronizados_todavia')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {(docs ?? []).map((doc) => (
            <Card key={doc.id}>
              <CardContent className="flex items-center gap-3 pt-3 pb-3">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.title || '—'}</p>
                  <p className="text-xs text-muted-foreground">{t('performance.adminknowledge.chunks_tipo', { n: doc.chunkCount ?? 0, type: doc.contentType })}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                  onClick={() => deleteDoc.mutateAsync({ docId: doc.id })}
                  disabled={deleteDoc.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
